import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDownIcon, ArrowUpDownIcon, ArrowUpIcon, CheckIcon, ChevronLeftIcon, ChevronRightIcon, FilterIcon, SearchIcon, XIcon } from 'lucide-react';
import { FollowUpRow } from './FollowUpRow';
import { LeadActionModal, type ActionMode } from './LeadActionModal';
import { availableCampaigns, followUpLeads, type FollowUpLead, type LoanPurpose } from '../data/followUps';
type StatusFilter = 'all' | 'pending' | 'contacted';
type SortKey = 'loanAmount' | 'lastContact';
type SortDir = 'asc' | 'desc';
const PAGE_SIZE = 5;
const purposeOptions: LoanPurpose[] = ['Purchase', 'Refinance'];
function toggleValue<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter(item => item !== value) : [...list, value];
}
function CheckOption({
  label,
  checked,
  onChange
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return <label className="flex cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50" data-id="element-238" data-mp="3otuv47ekdzea">
      <span aria-hidden="true" className={['flex h-4 w-4 items-center justify-center rounded border transition-colors', checked ? 'border-brand bg-brand text-white' : 'border-gray-300 bg-white'].join(' ')} data-id="element-239" data-mp="14hpu6jj7963">
        {checked && <CheckIcon className="h-3 w-3" strokeWidth={3} data-id="element-240" data-mp="64voixz8t6rg" />}
      </span>
      <input type="checkbox" checked={checked} onChange={onChange} className="sr-only" data-id="element-241" data-mp="ku1ud4sl5v8b" />
      <span className="text-[12.5px] text-ink" data-id="element-242" data-mp="fvqkiu2ajkq3">{label}</span>
    </label>;
}
type FollowUpQueueProps = {
  leads: FollowUpLead[];
  onUpdateLead: (id: string, changes: Partial<FollowUpLead>) => void;
};
export function FollowUpQueue({
  leads,
  onUpdateLead
}: FollowUpQueueProps) {
  const [status, setStatus] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [purposes, setPurposes] = useState<LoanPurpose[]>([]);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [page, setPage] = useState(1);
  const [action, setAction] = useState<{
    lead: FollowUpLead | null;
    mode: ActionMode;
  }>({
    lead: null,
    mode: 'call'
  });
  const filterRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!filtersOpen) return;
    const onClick = (event: MouseEvent) => {
      if (!filterRef.current?.contains(event.target as Node)) setFiltersOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [filtersOpen]);
  useEffect(() => {
    setPage(1);
  }, [status, search, purposes]);
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return leads.filter(lead => {
      if (status !== 'all' && lead.status !== status) return false;
      if (purposes.length > 0 && !purposes.includes(lead.loanPurpose)) return false;
      if (!query) return true;
      return lead.name.toLowerCase().includes(query) || lead.email.toLowerCase().includes(query) || lead.campaignName.toLowerCase().includes(query);
    });
  }, [leads, status, purposes, search]);
  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      const compare = sortKey === 'loanAmount' ? a.loanAmount - b.loanAmount : new Date(a.lastContact).getTime() - new Date(b.lastContact).getTime();
      return sortDir === 'asc' ? compare : -compare;
    });
  }, [filtered, sortKey, sortDir]);
  const totalRecords = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const paged = sorted.slice(startIndex, startIndex + PAGE_SIZE);
  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };
  const SortIcon = ({
    column
  }: {
    column: SortKey;
  }) => {
    if (sortKey !== column) return <ArrowUpDownIcon className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" data-id="element-243" data-mp="3tyyu76mvwjm8" />;
    return sortDir === 'asc' ? <ArrowUpIcon className="h-3.5 w-3.5 text-brand" aria-hidden="true" data-id="element-244" data-mp="2t5vmov4qtb9" /> : <ArrowDownIcon className="h-3.5 w-3.5 text-brand" aria-hidden="true" data-id="element-245" data-mp="qh711ox0htmg" />;
  };
  const handleSubmit = (leadId: string, mode: ActionMode, payload: Record<string, string>) => {
    if (mode === 'call') {
      onUpdateLead(leadId, {
        status: 'contacted',
        lastContact: new Date().toISOString()
      });
    } else if (mode === 'schedule' && payload.nextFollowUp) {
      onUpdateLead(leadId, {
        nextFollowUp: payload.nextFollowUp
      });
    }
    setAction({
      lead: null,
      mode: 'call'
    });
  };
  const tabs: {
    value: StatusFilter;
    label: string;
  }[] = [{
    value: 'all',
    label: 'All'
  }, {
    value: 'pending',
    label: 'Pending'
  }, {
    value: 'contacted',
    label: 'Completed'
  }];
  return <section className="overflow-hidden rounded-xl border border-gray-200 bg-white" data-id="element-246" data-mp="1jlb36ugqn4pm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-3.5" data-id="element-247" data-mp="1xcfun99ycqov">
        <div data-id="element-248" data-mp="1scjop4hp2p6o">
          <h2 className="text-[14px] font-semibold text-ink" data-id="element-249" data-mp="1sduwrgi6aj3l">Today's follow-ups</h2>
          <p className="mt-0.5 text-[12.5px] text-gray-500" data-id="element-250" data-mp="14mh233wjubfg">
            Borrowers that need your attention today.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2" data-id="element-251" data-mp="10ngobzsxhyzb">
          <div className="flex rounded-lg bg-gray-100 p-1" data-id="element-252" data-mp="1emg9uyl59xr9">
            {tabs.map(tab => <button key={tab.value} type="button" aria-pressed={status === tab.value} onClick={() => setStatus(tab.value)} className={['rounded-md px-3 py-1.5 text-[12.5px] font-medium transition-colors', status === tab.value ? 'bg-white text-ink shadow-sm' : 'text-gray-600 hover:text-ink'].join(' ')} data-id="element-253" data-mp="19m29m2psbs3s">
                {tab.label}
              </button>)}
          </div>

          <div className="relative" data-id="element-254" data-mp="371pkv4yagmo3">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" aria-hidden="true" data-id="element-255" data-mp="370mxv84374dg" />
            <input type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Search follow-ups" aria-label="Search follow-ups" className="w-48 rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-[12.5px] text-ink placeholder:text-gray-400 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" data-id="element-256" data-mp="2ecily4ux9v2b" />
          </div>

          <div className="relative" ref={filterRef} data-id="element-257" data-mp="1o39sy49ch9h8">
            <button type="button" onClick={() => setFiltersOpen(prev => !prev)} aria-expanded={filtersOpen} aria-haspopup="true" className={['inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-2 text-[12.5px] font-medium transition-colors', purposes.length > 0 || filtersOpen ? 'border-brand bg-brand-soft text-brand-dark' : 'border-gray-200 bg-white text-ink hover:bg-gray-50'].join(' ')} data-id="element-258" data-mp="1o1ynm0093to0">
              <FilterIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-259" data-mp="vdt5ll0l0irv" />
              Filters
              {purposes.length > 0 && <span className="ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-brand px-1 text-[10px] font-bold text-white" data-id="element-260" data-mp="qh2rh38c7x93">
                  {purposes.length}
                </span>}
            </button>

            {filtersOpen && <div role="menu" className="absolute right-0 z-30 mt-2 w-56 rounded-xl border border-gray-200 bg-white p-3 shadow-xl" data-id="element-261" data-mp="25ckgcojrzu15">
                <div className="flex items-center justify-between" data-id="element-262" data-mp="1ve4qeowhiqt8">
                  <h3 className="text-[12.5px] font-semibold text-ink" data-id="element-263" data-mp="20cg08zms51bg">Filters</h3>
                  {purposes.length > 0 && <button type="button" onClick={() => setPurposes([])} className="inline-flex items-center gap-1 text-[11.5px] font-medium text-brand hover:text-brand-dark" data-id="element-264" data-mp="1mkrpazo9oiw2">
                      <XIcon className="h-3 w-3" aria-hidden="true" data-id="element-265" data-mp="1cl0uewvso1vg" />
                      Clear
                    </button>}
                </div>
                <p className="mb-1.5 mt-2.5 text-[10.5px] font-semibold uppercase tracking-widest text-gray-400" data-id="element-266" data-mp="13mp5czaktc54">
                  Loan purpose
                </p>
                <div className="space-y-0.5" data-id="element-267" data-mp="oyoti98tciw0">
                  {purposeOptions.map(option => <CheckOption key={option} label={option} checked={purposes.includes(option)} onChange={() => setPurposes(prev => toggleValue(prev, option))} data-id="element-268" data-mp="k15h7evoeg6p" />)}
                </div>
              </div>}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto" data-id="element-269" data-mp="12zhth9hvw66i">
        <table className="w-full min-w-[900px] text-left" data-id="element-270" data-mp="12yq6uo69lisd">
          <thead className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500" data-id="element-271" data-mp="1gr62h3wu6ej9">
            <tr data-id="element-272" data-mp="1cq40ifbjbodl">
              <th scope="col" className="px-5 py-2.5 font-semibold" data-id="element-273" data-mp="1cpkthtnfakec">
                Borrower
              </th>
              <th scope="col" className="px-5 py-2.5 font-semibold" aria-sort={sortKey === 'loanAmount' ? sortDir === 'asc' ? 'ascending' : 'descending' : 'none'} data-id="element-274" data-mp="1qp3n7hfhlt74">
                <button type="button" onClick={() => toggleSort('loanAmount')} className="flex items-center gap-1 uppercase tracking-wider transition-colors hover:text-ink" data-id="element-275" data-mp="3c1afxlutx5vc">
                  Loan info <SortIcon column="loanAmount" data-id="element-276" data-mp="7lhpxq0vfw1p" />
                </button>
              </th>
              <th scope="col" className="px-5 py-2.5 font-semibold" data-id="element-277" data-mp="3ms2f4m4cy4c">
                Current campaign
              </th>
              <th scope="col" className="px-5 py-2.5 font-semibold" aria-sort={sortKey === 'lastContact' ? sortDir === 'asc' ? 'ascending' : 'descending' : 'none'} data-id="element-278" data-mp="3uu4o9wxy5zql">
                <button type="button" onClick={() => toggleSort('lastContact')} className="flex items-center gap-1 uppercase tracking-wider transition-colors hover:text-ink" data-id="element-279" data-mp="24b6428ot38gj">
                  Last contact <SortIcon column="lastContact" data-id="element-280" data-mp="2iz0zhwu77qxb" />
                </button>
              </th>
              <th scope="col" className="px-5 py-2.5 font-semibold" data-id="element-281" data-mp="2n0mje255h5h0">
                Next follow-up
              </th>
              <th scope="col" className="px-5 py-2.5 font-semibold" data-id="element-282" data-mp="2rz66w6ze6pbh">
                Status
              </th>
              <th scope="col" className="px-5 py-2.5 text-right font-semibold" data-id="element-283" data-mp="2wyirsm6pdmbf">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-[13px]" data-id="element-284" data-mp="31vsn2igrws76">
            {paged.map(lead => <FollowUpRow key={lead.id} lead={lead} campaigns={availableCampaigns} onAction={(id, mode) => setAction({
            lead: leads.find(item => item.id === id) ?? null,
            mode
          })} onAssignCampaign={(id, campaignName) => onUpdateLead(id, {
            campaignName
          })} data-id="element-285" data-mp="12n42u7linqwq" />)}
            {paged.length === 0 && <tr data-id="element-286" data-mp="14hzo665h6hfc">
                <td colSpan={7} className="px-5 py-12 text-center text-[13px] text-gray-500" data-id="element-287" data-mp="14hg4ol06fkz7">
                  No follow-ups match the current filters.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>

      {totalRecords > 0 && <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-5 py-3" data-id="element-288" data-mp="1s83l5uo5rixd">
          <p className="text-[12.5px] text-gray-500" data-id="element-289" data-mp="1na20547699j9">
            Showing{' '}
            <span className="font-medium text-ink" data-id="element-290" data-mp="217qlse92ckpd">
              {startIndex + 1}–{Math.min(startIndex + PAGE_SIZE, totalRecords)}
            </span>{' '}
            of <span className="font-medium text-ink" data-id="element-291" data-mp="1x53mxtdz5zio">{totalRecords}</span> follow-ups
          </p>
          <div className="flex items-center gap-2" data-id="element-292" data-mp="2fte6c0j6hr8r">
            <button type="button" onClick={() => setPage(prev => Math.max(1, prev - 1))} disabled={currentPage <= 1} className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[12.5px] font-medium text-ink transition-colors hover:bg-gray-50 disabled:opacity-40" data-id="element-293" data-mp="2kt8ys8ilqovx">
              <ChevronLeftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-294" data-mp="21vzdsmxmp3ck" />
              Previous
            </button>
            <span className="text-[12.5px] text-gray-600" data-id="element-295" data-mp="1o52ksuzsik4z">
              Page {currentPage} of {totalPages}
            </span>
            <button type="button" onClick={() => setPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage >= totalPages} className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[12.5px] font-medium text-ink transition-colors hover:bg-gray-50 disabled:opacity-40" data-id="element-296" data-mp="1s5wb1v0pgb11">
              Next
              <ChevronRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-297" data-mp="2hj50ok4mn1qo" />
            </button>
          </div>
        </div>}

      <LeadActionModal lead={action.lead} mode={action.mode} onClose={() => setAction({
      lead: null,
      mode: 'call'
    })} onSubmit={handleSubmit} data-id="element-298" data-mp="27rhht7m4yt0t" />
    </section>;
}